import { Type } from '@/src/constrains/ProductsSelection/ProductSelectionWithHsCode';
import {
  ILogisticsMethod,
  IPaymentStatus,
  RefundSummaryType,
} from '@/src/types/common/order';
import { paymentGatewayProvider } from '@/src/types/common/paymentGateway';
import { useRequest } from 'ahooks';
import { Table } from 'antd';
import { get } from 'lodash';
import React, { Fragment, useMemo } from 'react';
//import css from './css.module.less';
import { Collapse, Image, PopOver } from 'src/components/Antd';
import GlobalTooltip, {
  NoWrapContainer,
} from 'src/components/GlobalTooltip/GlobalTooltip';
import { SectionWrapper } from 'src/components/Layouts';
import ModalHoc from 'src/constrains/ModalHOC';
import { useI18n } from 'src/hooks/useRouterChange';
import { markAsPaid } from 'src/request/order';
import { firstUpperCase, isNumber } from 'src/tools/helper';
import { i18nGet } from 'src/tools/tools';
import { EDiscountType, EShipping } from 'src/types/common/discount';
import { EType } from 'src/types/common/order';
import ExclusiveCustom from '../../../Components/ExclusiveCustom';
import ReturnReject from '../../../Components/ReturnReject';
import SkuItems from '../../../Components/SkuItems';
import { EExternalRef } from '../tools';
import css from './css.module.less';

const tableName = 'OrderInfoLineItems';
const calcOrderBy = (transactionInfo): string => {
  const {
    paymentDetail,
    markAsPaid = false,
    paymentMethod = '',
  } = transactionInfo || {};
  const { brand } = paymentDetail || {};
  if (markAsPaid) {
    return 'Manually mark as paid';
  }
  if (brand) {
    return firstUpperCase(brand);
  }
  return paymentMethod;
};
const OrderInfo = (props) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);

  const TypeI18NKeyMapping = {
    ['RefundedItems']: 'allOrders.orderDetail.refundedItems',
    ['ShippingFee']: 'allOrders.orderDetail.shippingFee',
    ['ShippingFeeTax']: 'allOrders.orderDetail.shippingFeeTax',
    ['ProductTax']: 'allOrders.orderDetail.productTax',
    ['OrderTax']: 'allOrders.orderDetail.orderTax',
    ['Adjustment']: 'allOrders.orderDetail.adjustment',
    ['TotalRefund']: 'allOrders.orderDetail.totalRefund',
    ['NetPayment']: 'allOrders.orderDetail.netPayment',
  };
  const {
    orderInfo = {},
    modalOp: { setShowModal, setModalTitle, setModalContent },
    orderUuid,
    onUpdate,
    markAsPaidDiabled,
  } = props;
  const {
    orderLineItems = [],
    processingFee,
    amount,
    subTotal,
    totalPrice,
    discounts,
    taxes,
    comments,
    shippingPrice,
    giftCards,
    transactionInfo,
    paymentStatus,
    refundSummary,
    taxIncluded,
    paymentDetail = {},
    shippingTaxes = [],
    logisticsMethod,
    gatewayProvider,
    containPayUpFront,
    firstOrder,
  } = orderInfo;

  const unFisrtOrderConatinPayUpFront = !firstOrder && containPayUpFront;

  const paymentMethodName = transactionInfo?.paymentMethodName || '';

  const expandedRows = (orderLineItems || []).map((i) => i.uuid);

  const shippingTaxText: string = useMemo(() => {
    switch (logisticsMethod) {
      case ILogisticsMethod.ClickCollect:
        return getI18n('allOrders.orderDetail.clickCollect');
      case ILogisticsMethod.Delivery:
        return getI18n('allOrders.orderDetail.delivery');
      case ILogisticsMethod.ParcelLocker:
        return getI18n('allOrders.orderDetail.parcelLocker');
      default:
        return '-';
    }
  }, [logisticsMethod]);
  const refundSummaryList = (refundSummary || []).filter(
    (o) =>
      o.type !== RefundSummaryType.TotalRefund &&
      o.type !== RefundSummaryType.NetPayment,
  );

  const refundSummaryTotal = (refundSummary || []).filter(
    (o) =>
      o.type === RefundSummaryType.TotalRefund ||
      o.type === RefundSummaryType.NetPayment,
  );

  const { run: _markAsPaid } = useRequest(
    (data) => markAsPaid(orderUuid, data),
    {
      manual: true,
      onSuccess: (data) => {
        onUpdate(data);
        onModalCancel();
      },
    },
  );

  const columns = ({ getI18n }): any => [
    {
      title: getI18n('allOrders.orderDetail.orderInfo_columns_title'),
      key: 'productName',
      dataIndex: 'productName',
      width: 254,
      render: (text, record) => {
        // console.log('record', record);
        const {
          productName = '',
          additional = false,
          isCustom,
          payUpFrontVirtual,
        } = record || {};
        //console.log('record', record);
        const thumbnailUrl = get(record, 'image.url');
        const productNameText = `${productName}${
          additional
            ? ` (${getI18n('allOrders.orderDetail.additionalitem')})`
            : ''
        }`;
        const customisationItem = get(record, 'customisation.items') || [];
        const exclusiveCustomData = (customisationItem || []).map((item) => ({
          title: `${item.title} : ${(item.values || []).join(',')}`,
        }));

        const render = () => {
          if (isCustom) {
            return (
              <div className={css.exclusiveCustomInTable}>
                <ExclusiveCustom data={exclusiveCustomData} />
              </div>
            );
          } else if (payUpFrontVirtual) {
            return (
              <NoWrapContainer>
                <GlobalTooltip
                  title={
                    getI18n('allOrders.orderDetail.payupfrontName') || '-'
                  }>
                  <div className={css.text}>
                    {getI18n('allOrders.orderDetail.payupfrontName')}
                  </div>
                </GlobalTooltip>
              </NoWrapContainer>
            );
          } else {
            return (
              <div className={css.productNameContainer}>
                <Image
                  style={{
                    marginRight: 12,
                  }}
                  width={40}
                  height={40}
                  key={thumbnailUrl}
                  src={thumbnailUrl}
                  alt={thumbnailUrl}
                />
                <NoWrapContainer>
                  <GlobalTooltip title={productNameText || '-'}>
                    <div className={css.text}>{productNameText || '-'}</div>
                  </GlobalTooltip>
                </NoWrapContainer>
              </div>
            );
          }
        };

        return render();
      },
    },
    {
      title: getI18n('allOrders.orderDetail.orderInfo_columns_sku'),
      key: 'sku',
      dataIndex: 'sku',
      width: 142,
    },
    {
      title: getI18n('allOrders.orderDetail.orderInfo_columns_qty'),
      key: 'qty',
      align: 'right',
      render: (text, record, index) => {
        // console.log('record', record);
        const { amount } = record;
        return (
          <NoWrapContainer className={css.right}>
            <GlobalTooltip title={amount || '-'}>{amount}</GlobalTooltip>
          </NoWrapContainer>
        );
      },
      width: 72,
    },
    {
      title: (
        <div>
          <div>
            {getI18n('allOrders.orderDetail.orderInfo_columns_originalPrice')}
          </div>
          <div>
            (
            {taxIncluded
              ? getI18n(
                  'allOrders.orderDetail.orderInfo_columns_originalPrice_taxIncludedYes',
                )
              : getI18n(
                  'allOrders.orderDetail.orderInfo_columns_originalPrice_taxIncludedNo',
                )}
            .{' '}
            {getI18n(
              'allOrders.orderDetail.orderInfo_columns_originalPrice_taxIncluded',
            )}
            )
          </div>
        </div>
      ),
      key: 'Original',
      textWrap: 'word-break',
      align: 'right',
      render: (text, record) => {
        const { originalPrice } = record;
        const data = isNumber(originalPrice) ? originalPrice.toFixed(2) : '-';
        return (
          <NoWrapContainer className={css.right}>
            <GlobalTooltip title={data}>{data || '-'}</GlobalTooltip>
          </NoWrapContainer>
        );
      },
      width: 103,
    },
    {
      title: (
        <div>
          <div>
            {getI18n(
              'allOrders.orderDetail.productItems_ProductItemsTable_columns_Disc_title',
            )}
            .
          </div>
          <div>
            (
            {taxIncluded
              ? getI18n(
                  'allOrders.orderDetail.orderInfo_columns_originalPrice_taxIncludedYes',
                )
              : getI18n(
                  'allOrders.orderDetail.orderInfo_columns_originalPrice_taxIncludedNo',
                )}
            .{' '}
            {getI18n(
              'allOrders.orderDetail.orderInfo_columns_originalPrice_taxIncluded',
            )}
            )
          </div>
        </div>
      ),
      key: 'disc',
      align: 'right',
      render: (text, record) => {
        const { discountPrice } = record;
        const data = isNumber(discountPrice) ? discountPrice.toFixed(2) : '-';
        return (
          <NoWrapContainer className={css.right}>
            <GlobalTooltip title={data}>- {data || '-'}</GlobalTooltip>
          </NoWrapContainer>
        );
      },
    },
    {
      title: (
        <div>
          <div>
            {getI18n(
              'allOrders.orderDetail.orderInfo_columns_PaidPrice_taxIncludedYes',
            )}
          </div>
          <div>
            (
            {taxIncluded
              ? getI18n(
                  'allOrders.orderDetail.orderInfo_columns_originalPrice_taxIncludedYes',
                )
              : getI18n(
                  'allOrders.orderDetail.orderInfo_columns_originalPrice_taxIncludedNo',
                )}
            .{' '}
            {getI18n(
              'allOrders.orderDetail.orderInfo_columns_originalPrice_taxIncluded',
            )}
            )
          </div>
        </div>
      ),
      key: 'paid',
      align: 'right',
      render: (text, record) => {
        const { paidPrice, type } = record;
        if (type === EType.Gift || type === EType.SubscriptionBuyGift) {
          return (
            <span>
              {getI18n(
                'allOrders.orderDetail.orderInfo_columns_PaidPrice_judgegift',
              )}
            </span>
          );
        }
        const data = isNumber(paidPrice) ? paidPrice.toFixed(2) : '-';
        return (
          <NoWrapContainer className={`${css.paidPrice} ${css.right}`}>
            <GlobalTooltip title={data}>{data || '-'}</GlobalTooltip>
          </NoWrapContainer>
        );
      },
    },
  ];
  const onModalCancel = () => {
    setShowModal(false);
  };

  const handleMarkAsPaid = () => {
    setShowModal(true);
    setModalTitle(getI18n('allOrders.orderDetail.markasPaid'));
    setModalContent(
      <ReturnReject
        type='MarkAsPaid'
        onModalCancel={onModalCancel}
        onSubmit={(data) => _markAsPaid(data)}
      />,
    );
  };

  const addButtonOverlay = (
    <div className={css.overlay}>
      <div className={css.overlayItem}>
        <h3>{getI18n('general.status')}</h3>
        <span>{firstUpperCase((paymentDetail || {}).status || '')}</span>
      </div>
      <div className={css.overlayItem}>
        <h3>{getI18n('allOrders.orderDetail.paidat')}</h3>
        <span>{(paymentDetail || {}).paidAt}</span>
      </div>
      <div className={css.overlayItem}>
        <h3>
          {getI18n(
            'allOrders.orderDetail.virtualproduct_returndata_options_judgeTypeGiftcard_amounttitle',
          )}
        </h3>
        <span>$ {(paymentDetail || {}).amount}</span>
      </div>
      <div className={css.overlayItem}>
        <h3>
          {getI18n(
            'allOrders.createneworder.components_OrderInfoRight_return_Currency',
          )}
        </h3>
        <span>{(paymentDetail || {}).currency}</span>
      </div>
      <div className={css.overlayItem}>
        <h3>{getI18n('allOrders.orderDetail.externalReference')}</h3>
        <span>{(paymentDetail || {}).gatewayReferenceId}</span>
      </div>
    </div>
  );
  const bankTransferOverlay = (
    <div className={css.overlay}>
      <div className={css.overlayItem}>
        <h3>{getI18n('allOrders.orderDetail.markedaspaidat')}</h3>
        <span>{(paymentDetail || {}).paidAt}</span>
      </div>
      <div className={css.overlayItem}>
        <h3>{getI18n('allOrders.orderDetail.externalReference')} </h3>
        <span>{(paymentDetail || {}).gatewayReferenceId}</span>
      </div>
      <div className={css.overlayItem}>
        <h3>
          {getI18n('allOrders.orderDetail.refund_return_refunditem_handleby')}
        </h3>
        <span>{(paymentDetail || {}).handledBy}</span>
      </div>
    </div>
  );
  /**Discount的细节展示 */
  const discountsCustomData = (discounts || []).map((item) => {
    const _name = get(item, 'name');

    const isVoucher = get(item, 'voucher');
    const _subscriptionBuy = get(item, 'subscriptionBuy')
      ? `${getI18n(
          'subscriptionBuy.subscriptionBuy.list_pageLayoutProps_title',
        )} `
      : '';

    const _voucher = isVoucher
      ? `${getI18n('allOrders.orderDetail.voucher')} `
      : '';
    const _direct = item.direct
      ? `${getI18n('allOrders.orderDetail.directDiscount')} `
      : '';
    const _flashSale = item.flashSale
      ? `${getI18n('allOrders.orderDetail.flashSale')} `
      : '';

    const buysQty = get(item, 'discountMethod.buysQty', '');

    const _buysQty = buysQty || '';

    const _discountType = get(item, 'discountMethod.discountType');
    const _discountValue = get(item, 'discountMethod.discountValue');
    const _deduction = get(item, 'discountMethod.deduction');
    const _reductionAmount = get(item, 'discountMethod.reductionAmount');
    const _percentage = get(item, 'discountMethod.percentage');
    const _fixedAmount = get(item, 'discountMethod.fixedAmount');
    const _shippingDiscountType = get(
      item,
      'discountMethod.shippingDiscountType',
    );

    return {
      title: `${_name} (${_subscriptionBuy}${_voucher}${_direct}${_flashSale} ${
        _buysQty && getI18n('allOrders.orderDetail.buy')
      } ${_buysQty && buysQty} ${
        _buysQty && getI18n('allOrders.orderDetail.get')
      }  ${_buysQty && get(item, 'discountMethod.getsQty', '')}  ${
        _buysQty && getI18n('allOrders.orderDetail.for')
      } ${
        _discountType == EDiscountType.Reduction ||
        _discountValue == EDiscountType.Reduction
          ? `${getI18n('allOrders.orderDetail.fixedReduction')} - ${
              _deduction || _reductionAmount
            }`
          : _discountType == EDiscountType.Percentage ||
            _discountValue == EDiscountType.Percentage
          ? `${_percentage}% ${getI18n('allOrders.orderDetail.oFF')}`
          : _discountType == EDiscountType.Shipping
          ? _shippingDiscountType === EShipping.Free
            ? getI18n('allOrders.orderDetail.freeShipping')
            : `${getI18n(
                'allOrders.orderDetail.sippingFeeDiscount',
              )} - ${getI18n(
                'allOrders.orderDetail.fixedReductionAmount',
              )} ${_fixedAmount}`
          : _discountType == EDiscountType.Gift
          ? getI18n('allOrders.orderDetail.freeGift')
          : _discountValue || ''
      })`,
      subTitle: get(item, 'code')
        ? `${getI18n('allOrders.orderDetail.promocode')}: ${item.code}`
        : get(item, 'voucherNo')
        ? `${getI18n('allOrders.orderDetail.voucherNo')}: ${item.voucherNo}`
        : null,
    };
  });

  const orderTax = (taxes || []).filter((o) => o.applyTo !== Type.Shipping);

  const expandableData = orderLineItems.reduce((pre, now) => {
    if (get(now, 'customisation.items', []).length) {
      const newData = [{ ...now, haveCustom: true }];
      newData.push({
        ...(now || {}).customisationItem,
        productName: '',
        isCustom: true,
        customisation: { items: get(now, 'customisation.items') },
      });
      return pre.concat(newData);
    }
    return pre.concat([now]);
  }, []);
  return (
    <SectionWrapper
      title={getI18n('allOrders.orderDetail.orderinfo_return_section_title')}
      containerClassName={css.containerClassName}>
      <Collapse
        title={getI18n('allOrders.orderDetail.orderinfo_return_collapse_title')}
        titleStyle={{ boxShadow: 'none' }}
        titleContainerClassName={css.lineItemsTitle}
        content={
          <Table
            rowKey='uuid'
            pagination={false}
            dataSource={expandableData}
            rowClassName={(record, index) =>
              record.haveCustom
                ? css.haveCustom
                : record.isCustom
                ? css.isCustom
                : css.noCustom
            }
            className={css.detailTable}
            id={tableName}
            columns={columns({ getI18n })}
            expandable={{
              expandedRowClassName: (record) => {
                const isParent = get(record, EExternalRef.isParent);
                const isChild = get(record, EExternalRef.isChild);
                const isLast = get(record, EExternalRef.isLast);
                //console.log(record);
                const shouldHideBottomLine =
                  ((isParent || isChild) && !isLast) ||
                  (record || {}).haveCustom;
                return `${css.expandedRow} ${
                  shouldHideBottomLine ? css.hideBottomLine : ''
                }`;
              },
              expandedRowRender: (record) => {
                const isChild = get(record, EExternalRef.isChild);
                const skuItems = get(record, 'simpleProducts') || [];
                const optionValues = get(record, 'optionValues') || [];
                const skuItemsOptions = skuItems.map((item) => [
                  item.productName,
                  item.sku,
                ]);
                if (isChild) {
                  return <React.Fragment />;
                }
                if (!skuItems.length && !optionValues.length) {
                  return <React.Fragment />;
                }
                return (
                  <React.Fragment>
                    <div className={css.optionValues}>
                      {optionValues.map((item, index) => (
                        <span key={index}>
                          {item.label}:{item.value},
                        </span>
                      ))}
                    </div>
                    {!!(skuItemsOptions || []).length && (
                      <div className={css.customizationLine}>
                        <div className={css.skuItems}>
                          <SkuItems data={skuItemsOptions} />
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              },
              expandIconColumnIndex: -1,
              indentSize: 0,
              expandedRowKeys: expandedRows,
            }}
          />
        }
      />
      <div className={css.orderBottom}>
        {comments ? (
          <div className={css.orderComments}>
            <h3>
              {getI18n('allOrders.orderDetail.orderinfo_return_ordercomments')}
            </h3>
            <p>{comments}</p>
          </div>
        ) : null}
        <div className={css.totalItem}>
          <div className={css.totalContain}>
            <span className={css.first}>
              {getI18n('allOrders.orderDetail.orderinfo_return_subtotal')}
            </span>
            <div className={css.exclusive}>
              {amount} {getI18n('general.items')}
            </div>
            <span className={css.last}>{subTotal}</span>
          </div>
          {(discounts || []).length ? (
            <div className={css.totalContain}>
              <span className={css.first}>
                {getI18n('allOrders.orderDetail.orderinfo_return_discounts')}
              </span>
              <div className={css.exclusive}>
                {discountsCustomData.map((item) => (
                  <ExclusiveCustom
                    // key={item.uuid}
                    data={[item]}
                    className={css.exclusiveCustom}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
        {(orderTax || []).length ? (
          <div className={css.totalItem}>
            {taxes.map((item) => (
              <div key={item.uuid} className={css.totalContain}>
                <span className={css.first}>{item.name}</span>
                <div className={css.exclusive}>({item.percentage}%)</div>
                <span className={css.last}>{item.totalPrice}</span>
              </div>
            ))}
          </div>
        ) : null}
        {shippingTaxes.length || shippingPrice ? (
          <div className={css.totalItem}>
            {shippingPrice && (
              <div className={css.totalContain}>
                <span className={css.first}>
                  {getI18n(
                    'allOrders.orderDetail.orderinfo_return_shippingtaxs_cssfirst',
                  )}
                </span>
                <div className={css.exclusive}>{shippingTaxText}</div>
                <span className={css.last}>{shippingPrice}</span>
              </div>
            )}
            {shippingTaxes.map((item) => (
              <div key={item.uuid} className={css.totalContain}>
                <span className={css.first}>{item.name}</span>
                <div className={css.exclusive}>({item.percentage}%)</div>
                <span className={css.last}>{item.totalPrice}</span>
              </div>
            ))}
          </div>
        ) : null}
        {processingFee ? (
          <div className={css.totalItem}>
            <div className={css.totalContain}>
              <span className={css.first}>
                {getI18n('allOrders.orderDetail.processingFe')}
              </span>
              <div className={css.exclusive}></div>
              <span className={css.last}>{processingFee}</span>
            </div>
          </div>
        ) : null}
        <div className={css.totalItem}>
          <div className={css.totalContain}>
            <span className={css.firstTitle}>
              {getI18n('allOrders.orderDetail.total')} (
              {taxIncluded
                ? getI18n(
                    'allOrders.orderDetail.orderInfo_columns_originalPrice_taxIncludedYes',
                  )
                : getI18n(
                    'allOrders.orderDetail.orderInfo_columns_originalPrice_taxIncludedNo',
                  )}
              .{' '}
              {getI18n(
                'allOrders.orderDetail.orderInfo_columns_originalPrice_taxIncluded',
              )}
              )
            </span>
            <div className={css.exclusive}></div>
            <span className={css.last}>{totalPrice}</span>
          </div>
          {/* {giftCards.map((item) => (
            <div className={css.totalContain}>
              <span className={css.first}>Gift Card</span>
              <div className={css.exclusive}>
                <ExclusiveCustom
                  className={css.exclusiveCustom}
                  title='專屬定制：刻字”XXX”'
                  subTitle='Promo code: DCXRKFJSMAWI'
                />
              </div>
              <span className={css.last}>-100.00</span>
            </div>
          ))} */}
          {(giftCards || []).map((item) => (
            <div key={item.uuid} className={css.totalContain}>
              <span className={css.first}>
                {getI18n(
                  'allOrders.orderDetail.orderinfo_return_giftcards_cssfirst',
                )}
              </span>
              <div className={css.exclusive}>
                <ExclusiveCustom
                  className={css.exclusiveCustom}
                  data={[
                    {
                      //allOrders.orderDetail.orderinfo_return_giftcards_cssfirst
                      title: `${
                        item.sku
                          ? 'SKU'
                          : getI18n(
                              'giftCard.giftCardList.giftCardAndStoreCredit_List_config_cardnumber',
                            )
                      }:${item.sku || item.cardNo}`,
                      subTitle: `${getI18n(
                        'allOrders.orderDetail.orderinfo_return_giftcards_exclusivecustom_subtitle',
                      )} ${item.redemptionCode}`,
                    },
                  ]}
                />
              </div>
              <span className={css.last}>{item.used}</span>
            </div>
          ))}
          {/* <div className={css.totalContain}>
            <span className={css.first}>Gift Card</span>
            <div className={css.exclusive}>
              <ExclusiveCustom
                className={css.exclusiveCustom}
                title='專屬定制：刻字”XXX”'
                subTitle='Promo code: DCXRKFJSMAWI'
              />
            </div>
            <span className={css.last}>-100.00</span>
          </div> */}

          {/* {get(transactionInfo, 'provider', '') ===
          paymentGatewayProvider.BANK_TRANSFER ? ( */}
          {paymentStatus === IPaymentStatus.PENDING ? (
            <div className={css.totalContain}>
              <span className={css.first}>
                {getI18n('allOrders.orderDetail.paymentPending')}
              </span>
              <div className={css.exclusive}>
                <div>
                  {getI18n('allOrders.orderDetail.paymentgatewayname')}(
                  {gatewayProvider})
                </div>
                {paymentStatus == IPaymentStatus.PENDING &&
                  gatewayProvider === paymentGatewayProvider.BANK_TRANSFER && (
                    <div
                      className={`${markAsPaidDiabled ? css.disabled : ''} ${
                        css.visaNumber
                      }`}
                      onClick={markAsPaidDiabled ? null : handleMarkAsPaid}>
                      {getI18n('allOrders.orderDetail.markasPaid')}
                    </div>
                  )}
              </div>
              <span className={css.last}>
                {get(transactionInfo, 'paidPrice', '')}
              </span>
            </div>
          ) : gatewayProvider ? (
            <div className={css.totalContain}>
              <span className={css.first}>
                {getI18n('allOrders.orderDetail.customerPaid')}
              </span>
              <div className={css.exclusive}>
                {unFisrtOrderConatinPayUpFront ? (
                  <div>
                    {getI18n('allOrders.orderDetail.prepaidsubscription')}
                  </div>
                ) : (
                  <div>
                    {getI18n('allOrders.orderDetail.paymentgatewayname')}(
                    {gatewayProvider})
                  </div>
                )}

                {gatewayProvider !== paymentGatewayProvider.BANK_TRANSFER ? (
                  <PopOver
                    className={css.dropDown}
                    placement='bottom'
                    overlay={
                      transactionInfo.markAsPaid
                        ? bankTransferOverlay
                        : addButtonOverlay
                    }
                    trigger='hover'>
                    <div className={css.visaNumber}>
                      {`${getI18n('allOrders.orderDetail.by')} `}
                      {unFisrtOrderConatinPayUpFront
                        ? paymentMethodName
                        : calcOrderBy(transactionInfo)}
                    </div>
                  </PopOver>
                ) : (
                  <PopOver
                    className={css.dropDown}
                    placement='bottom'
                    overlay={bankTransferOverlay}
                    trigger='hover'>
                    <div className={css.visaNumber}>
                      {getI18n('allOrders.orderDetail.by')} {gatewayProvider}
                    </div>
                  </PopOver>
                )}
              </div>
              <span className={css.last}>
                {get(transactionInfo, 'paidPrice', '')}
              </span>
            </div>
          ) : null}
        </div>
        {(refundSummaryList || []).length ? (
          <Fragment>
            <div className={css.totalItem}>
              <div className={css.totalContain}>
                <span className={css.firstTitle}>
                  {getI18n('allOrders.orderDetail.refund')}
                </span>
                <div className={css.exclusive}></div>
                <span className={css.last}></span>
              </div>
              {(refundSummaryList || []).map((item) => (
                <div key={item.type} className={css.totalContain}>
                  <span className={css.first}>
                    {getI18n(TypeI18NKeyMapping[item.type])}
                  </span>

                  <div className={css.exclusive}>
                    {item.count
                      ? `${item.count} ${getI18n('general.items')}`
                      : null}
                  </div>
                  <span className={css.last}>
                    {item.type !== RefundSummaryType.Adjustment ? '-' : ''}{' '}
                    {item.amount}
                  </span>
                </div>
              ))}
            </div>

            <div className={css.totalItem}>
              {(refundSummaryTotal || []).map((item) => (
                <div key={item.type} className={css.totalContain}>
                  <span className={css.firstTitle}>
                    {getI18n(TypeI18NKeyMapping[item.type])}
                  </span>
                  <div className={css.exclusive}></div>
                  <span className={css.last}>
                    {item.type === RefundSummaryType.TotalRefund ? '-' : null}{' '}
                    {item.amount}
                  </span>
                </div>
              ))}
            </div>
          </Fragment>
        ) : null}
      </div>
    </SectionWrapper>
  );
};
export default ModalHoc(OrderInfo);
